import axios from "axios";
import { useEffect, useState, useCallback } from "react";

// Central API client. Authentication rides an httpOnly session cookie set by
// the backend (immune to XSS token theft - no token ever touches JS storage).
// The active project scope (X-Project-Id, not sensitive) is attached per
// request; 401s force a clean re-login.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

api.interceptors.request.use((config) => {
  const pid = localStorage.getItem("tc_project_id");
  if (pid) config.headers["X-Project-Id"] = pid;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!path) { setLoading(false); return; }  // conditional fetch: skip when path is null
    try {
      setLoading(true);
      const res = await api.get(path);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.message || "Request failed");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, ...deps]);

  return { data, loading, error, refetch };
}
